import React, {useEffect, useState} from 'react';
import {Alert, Button, Form, Input, message, Modal, Space, Tag, Tooltip} from 'antd';
import {BulbOutlined, CloseOutlined, EditOutlined, PlusOutlined, SaveOutlined} from '@ant-design/icons';
import {createProjectWord, suggestWord, updateProjectWord} from '../../api/projectWordApi';

const { Search } = Input;

function ProjectWordCreateModal({ isOpen, onClose, onSuccess, pjtId, mode = 'create', initialValues = null }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  // 추천 라벨의 기준 단어명을 추천 생성 시점으로 고정하기 위한 상태
  const [suggestBaseWordNm, setSuggestBaseWordNm] = useState('');

  useEffect(() => {
    if (isOpen) {
      setError('');
      if (mode === 'edit' && initialValues) {
        form.setFieldsValue({
          wordNm: initialValues.wordNm ?? '',
          wordEngAbbrNm: initialValues.wordEngAbbrNm ?? '',
          wordEngNm: initialValues.wordEngNm ?? '',
          wordExpln: initialValues.wordExpln ?? '',
          domainClsfNm: initialValues.domainClsfNm ?? '',
          synm: initialValues.synm ?? '',
          phwrd: initialValues.phwrd ?? '',
        });
      } else {
        form.resetFields();
        setSuggestions([]);
        setSuggestBaseWordNm('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mode, initialValues]);

  const handleSubmit = async (values) => {
    if (!pjtId) {
      setError('프로젝트 정보가 없습니다.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      if (mode === 'edit' && initialValues?.pjtWordId) {
        await updateProjectWord(pjtId, initialValues.pjtWordId, values);
      } else {
        await createProjectWord(pjtId, values);
      }
      form.resetFields();
      setSuggestions([]);
      setSuggestBaseWordNm('');
      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      setError(((mode === 'edit') ? '수정에 실패했습니다: ' : '등록에 실패했습니다: ') + (err?.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setSuggestions([]);
    setSuggestBaseWordNm('');
    setError('');
    onClose();
  };

  const applySuggestion = (s) => {
    if (!s) return;
    const patch = {};
    if (s.wordEngAbbrNm) patch.wordEngAbbrNm = s.wordEngAbbrNm;
    if (s.wordEngNm) patch.wordEngNm = s.wordEngNm;
    if (s.wordExpln) patch.wordExpln = s.wordExpln;
    if (Object.keys(patch).length > 0) {
      form.setFieldsValue(patch);
    }
  };

  // onSearch 핸들러: clear(x)로 트리거된 경우는 무시
  const handleAiSuggest = async (value, _e, info) => {
    try {
      if (aiLoading) return; // 연타 방지
      if (info?.source === 'clear') return; // 초기화 버튼으로 인한 트리거는 무시
      if (!pjtId) {
        message.warning('프로젝트 정보가 없습니다.');
        return;
      }
      // 현재 입력 값을 우선 사용하고, 없으면 폼 값 사용
      const currentWordNm = (value ?? form.getFieldValue('wordNm'))?.toString().trim();
      const domainClsfNm = form.getFieldValue('domainClsfNm');
      if (!currentWordNm) {
        message.warning('먼저 단어명을 입력해주세요.');
        return;
      }
      setAiLoading(true);
      const result = await suggestWord(pjtId, { wordNm: currentWordNm, domainClsfNm });
      const data = result.data;
      const arr = Array.isArray(data) ? data : (data ? [data] : []);
      const top5 = arr.slice(0, 5);
      setSuggestions(top5);
      setSuggestBaseWordNm(currentWordNm || '');
      if (top5.length === 0) {
        message.info('AI가 추천할 내용을 찾지 못했습니다.');
      }
    } catch (err) {
      // 에러 토스트는 공통 인터셉터에서 처리
    } finally {
      setAiLoading(false);
    }
  };

  const isEdit = mode === 'edit';

  return (
    <Modal
      title={
        <Space>
          {isEdit ? <EditOutlined /> : <PlusOutlined />}
          <span>{isEdit ? '프로젝트 단어 수정' : '프로젝트 단어 신규생성'}</span>
        </Space>
      }
      open={isOpen}
      onCancel={handleClose}
      width={900}
      footer={null}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}
        initialValues={{ wordNm:'', wordEngAbbrNm:'', wordEngNm:'', wordExpln:'', domainClsfNm:'', synm:'', phwrd:'' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Form.Item label="단어명" name="wordNm" rules={[{ required: true, message: '단어명을 입력해주세요' }]}>
            <Search
              placeholder="예: 고객"
              allowClear
              enterButton={
                <Button type="primary" icon={<BulbOutlined />} loading={aiLoading} htmlType="button">
                  AI추천
                </Button>
              }
              onSearch={handleAiSuggest}
            />
          </Form.Item>
          <Form.Item label="영문약어" name="wordEngAbbrNm" rules={[{ required: true, message: '영문약어를 입력해주세요' }]}>
            <Input placeholder="예: CUST" />
          </Form.Item>
          <Form.Item label="영문명" name="wordEngNm" rules={[{ required: true, message: '영문명을 입력해주세요' }]}>
            <Input placeholder="예: CUSTOMER" />
          </Form.Item>
          <Form.Item label="도메인" name="domainClsfNm">
            <Input placeholder="예: 공통" />
          </Form.Item>

          {/* AI 추천 태그 */}
          <div style={{ gridColumn: '1 / span 2' }}>
            {suggestions.length > 0 && (
              <div>
                <div style={{ fontWeight: 600, color: '#555', marginBottom: 8 }}>AI 추천</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {suggestions.map((s, idx) => {
                    // 라벨의 단어명은 추천 생성 시점의 입력값으로 고정
                    const baseWordNm = (suggestBaseWordNm || '').toString().trim();
                    const abbr = (s.wordEngAbbrNm || '-').toString();
                    const eng = (s.wordEngNm || '-').toString();
                    const expl = (s.wordExpln || '-').toString();
                    const label = `${baseWordNm || '-'} | ${abbr} | ${eng} | ${expl}`;
                    return (
                      <Tooltip
                        key={idx}
                        placement="top"
                        title={
                          <div>
                            <div><strong>영문명:</strong> {eng}</div>
                            <div><strong>설명:</strong> {expl}</div>
                          </div>
                        }
                      >
                        <Tag color="geekblue" onClick={() => applySuggestion(s)} style={{ cursor: 'pointer', userSelect: 'none' }}>
                          {label}
                        </Tag>
                      </Tooltip>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <Form.Item label="설명" name="wordExpln" rules={[{ required: true, message: '설명을 입력해주세요' }]} style={{ gridColumn: '1 / span 2' }}>
            <Input.TextArea rows={3} placeholder="단어에 대한 설명을 입력하세요" />
          </Form.Item>
          <Form.Item label="동의어" name="synm">
            <Input placeholder="예: 사용자" />
          </Form.Item>
          <Form.Item label="금칙어" name="phwrd">
            <Input placeholder="예: 금지어" />
          </Form.Item>
        </div>

        {error && (
          <Alert message="오류" description={error} type="error" showIcon style={{ marginBottom: 16 }} />
        )}

        <div style={{ textAlign: 'right' }}>
          <Button icon={<CloseOutlined />} onClick={handleClose} style={{ marginRight: 8 }}>
            취소
          </Button>
          <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading}>
            {isEdit ? '수정' : '저장'}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}

export default ProjectWordCreateModal;
